import React, { useState, useEffect, useRef, Fragment } from 'react'
import QuestHOC from './QuestHOC'
import { PartnerCard, PartnerEditor } from './PartnerInfoEditor'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const MAX_PARTNERS = 6

const truncateLabel = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

const createFreshPartner = (sequenceNumber = 0) => ({
  title: '',
  description: '',
  logoFileName: '',
  name: '',
  sequenceNumber,
  _tempId: `partner-${Date.now()}-${Math.random()}`,
})

const hasPartnerContent = (partner) =>
  !!(
    partner?.title?.trim() ||
    partner?.description?.trim() ||
    partner?.logoFileName
  )

const mergePartnersFromQuest = (partnershipInfo, partnerships) => {
  const infos = Array.isArray(partnershipInfo) ? [...partnershipInfo] : []
  const logos = Array.isArray(partnerships) ? [...partnerships] : []

  if (infos.length === 0 && logos.length === 0) {
    return []
  }

  const sortedInfos = infos
    .map((pi, index) => ({
      title: pi?.title || '',
      description: pi?.description || '',
      sequenceNumber:
        typeof pi?.sequenceNumber === 'number' ? pi.sequenceNumber : index,
    }))
    .sort((a, b) => a.sequenceNumber - b.sequenceNumber)

  const maxLen = Math.max(sortedInfos.length, logos.length)
  const merged = []

  for (let i = 0; i < maxLen; i++) {
    const info = sortedInfos[i] || {
      title: '',
      description: '',
      sequenceNumber: i,
    }
    const logo = logos[i] || { logoFileName: '', name: '' }
    merged.push({
      title: info.title || logo.name || '',
      description: info.description || '',
      logoFileName: logo.logoFileName || '',
      name: logo.name || info.title || '',
      sequenceNumber: i,
      _tempId: `partner-${Date.now()}-${i}`,
    })
  }

  return merged
}

const cleanPartnersForAPI = (partners) => {
  const nonEmpty = partners.filter(hasPartnerContent)

  const partnershipInfo = nonEmpty
    .filter((p) => p.title?.trim() || p.description?.trim())
    .map((p, index) => ({
      title: (p.title || '').toString(),
      description: (p.description || '').toString(),
      sequenceNumber: index,
    }))

  const partnerships = nonEmpty
    .filter((p) => p.logoFileName && (p.title?.trim() || p.name?.trim()))
    .map((p) => ({
      logoFileName: p.logoFileName,
      name: (p.title || p.name || '').toString(),
    }))

  return { partnershipInfo, partnerships }
}

const adjustEditingIndex = (dragIndex, hoverIndex, currentIndex) => {
  if (typeof currentIndex !== 'number') {
    return currentIndex
  }
  if (currentIndex === dragIndex) {
    return hoverIndex
  }
  if (dragIndex < currentIndex && hoverIndex >= currentIndex) {
    return currentIndex - 1
  }
  if (dragIndex > currentIndex && hoverIndex <= currentIndex) {
    return currentIndex + 1
  }
  return currentIndex
}

const QuestPartners = ({
  questDetail,
  updateQuestDetails,
  isFormSubmitting,
}) => {
  const [partners, setPartners] = useState([])
  const partnersRef = useRef(partners)
  const [partnerEditor, setPartnerEditor] = useState({
    open: false,
    partner: null,
    partnerIndex: null,
  })
  const [uploadingIndex, setUploadingIndex] = useState(null)
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    index: null,
    label: '',
  })

  useEffect(() => {
    partnersRef.current = partners
  }, [partners])

  useEffect(() => {
    setPartners(
      mergePartnersFromQuest(
        questDetail?.partnershipInfo,
        questDetail?.partnerships,
      ),
    )
  }, [questDetail?.partnershipInfo, questDetail?.partnerships])

  useEffect(() => {
    if (!partnerEditor.open) {
      return undefined
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closePartnerEditor()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [partnerEditor.open])

  const closePartnerEditor = () => {
    setPartnerEditor({
      open: false,
      partner: null,
      partnerIndex: null,
    })
    setUploadingIndex(null)
  }

  const persistPartners = (nextPartners, options = {}) => {
    const cleaned = nextPartners.filter(hasPartnerContent)
    setPartners(cleaned)
    const payload = cleanPartnersForAPI(cleaned)
    updateQuestDetails(payload, options.silent ? { silent: true } : undefined)
  }

  const requestPartnerDelete = (idx) => {
    const partner = partners[idx]
    setDeleteModal({
      show: true,
      index: idx,
      label: partner?.title?.trim() || partner?.name?.trim() || 'Untitled',
    })
  }

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, index: null, label: '' })
  }

  const confirmPartnerDelete = () => {
    const idx = deleteModal.index
    closeDeleteModal()
    if (typeof idx !== 'number') return
    if (partnerEditor.open && partnerEditor.partnerIndex === idx) {
      closePartnerEditor()
    }
    const next = partners.filter((_, index) => index !== idx)
    persistPartners(next)
  }

  const handlePartnerEdit = (partner, index) => {
    if (partnerEditor.open && partnerEditor.partnerIndex === index) {
      closePartnerEditor()
      return
    }
    setPartnerEditor({
      open: true,
      partner,
      partnerIndex: index,
    })
  }

  const handleAddPartner = () => {
    if (partners.length >= MAX_PARTNERS) {
      return
    }
    const freshPartner = createFreshPartner(partners.length)
    setPartnerEditor({
      open: true,
      partner: freshPartner,
      partnerIndex: partners.length,
    })
  }

  const handlePartnerMove = (dragIndex, hoverIndex) => {
    const next = [...partnersRef.current]
    const [moved] = next.splice(dragIndex, 1)
    next.splice(hoverIndex, 0, moved)
    const updated = next.map((partner, index) => ({
      ...partner,
      sequenceNumber: index,
    }))

    partnersRef.current = updated
    setPartners(updated)

    if (partnerEditor.open) {
      const nextIndex = adjustEditingIndex(
        dragIndex,
        hoverIndex,
        partnerEditor.partnerIndex,
      )
      if (nextIndex !== partnerEditor.partnerIndex) {
        setPartnerEditor((prev) => ({
          ...prev,
          partnerIndex: nextIndex,
          partner:
            typeof nextIndex === 'number' ? updated[nextIndex] : prev.partner,
        }))
      }
    }
  }

  const persistAfterReorder = () => {
    updateQuestDetails(cleanPartnersForAPI(partnersRef.current), {
      silent: true,
    })
  }

  const handlePartnerSave = (partnerData) => {
    const { partnerIndex } = partnerEditor
    const isNew = partnerIndex >= partners.length

    if (isNew && !hasPartnerContent(partnerData)) {
      closePartnerEditor()
      return
    }

    const normalized = {
      ...partnerData,
      name: partnerData.title || partnerData.name || '',
    }

    if (partnerIndex < partners.length) {
      const updated = [...partners]
      updated[partnerIndex] = {
        ...normalized,
        _tempId: updated[partnerIndex]._tempId,
        sequenceNumber: partnerIndex,
      }
      persistPartners(updated)
    } else {
      const newPartner = {
        ...normalized,
        _tempId: `partner-${Date.now()}-${partners.length}`,
        sequenceNumber: partners.length,
      }
      persistPartners([...partners, newPartner])
    }
    closePartnerEditor()
  }

  const isAdding =
    partnerEditor.open && partnerEditor.partnerIndex >= partners.length

  const deleteName = truncateLabel(deleteModal.label)
  const deleteMessage = deleteName
    ? `\u201C${deleteName}\u201D will be removed. They will be removed from this experience.`
    : 'They will be removed from this experience.'

  return (
    <div data-add-list='partners'>
      {partners.length === 0 && !partnerEditor.open ? null : (
        <>
          {partners.map((partner, index) => {
            const isEditing =
              partnerEditor.open && partnerEditor.partnerIndex === index
            return (
              <Fragment key={partner._tempId || index}>
                <PartnerCard
                  partner={partner}
                  index={index}
                  isEditing={isEditing}
                  onEdit={handlePartnerEdit}
                  onDelete={requestPartnerDelete}
                  moveItem={handlePartnerMove}
                  onDragEnd={persistAfterReorder}
                  isUploading={uploadingIndex === index}
                />
                {isEditing ? (
                  <div className='experience-add-form'>
                    <PartnerEditor
                      key={partner._tempId || `edit-${index}`}
                      partner={partnerEditor.partner}
                      partnerIndex={partnerEditor.partnerIndex}
                      onSave={handlePartnerSave}
                      onCancel={closePartnerEditor}
                      isSaving={isFormSubmitting}
                      onUploadingChange={(busy) =>
                        setUploadingIndex(busy ? index : null)
                      }
                    />
                  </div>
                ) : null}
              </Fragment>
            )
          })}

          {isAdding ? (
            <div className='experience-add-form'>
              <PartnerEditor
                key='new-partner'
                partner={partnerEditor.partner}
                partnerIndex={partnerEditor.partnerIndex}
                onSave={handlePartnerSave}
                onCancel={closePartnerEditor}
                isSaving={isFormSubmitting}
                onUploadingChange={(busy) =>
                  setUploadingIndex(busy ? partners.length : null)
                }
              />
            </div>
          ) : null}
        </>
      )}

      {!partnerEditor.open ? (
        <div className='experience-form-actions'>
          <button
            type='button'
            className='experience-btn-sm btn-outline-experience'
            onClick={handleAddPartner}
            disabled={partners.length >= MAX_PARTNERS}>
            Add partner
          </button>
        </div>
      ) : null}

      <ExperienceConfirmModal
        show={deleteModal.show}
        onHide={closeDeleteModal}
        onConfirm={confirmPartnerDelete}
        title='Delete this partner?'
        message={deleteMessage}
        confirmLabel='Delete'
        isLoading={isFormSubmitting}
      />
    </div>
  )
}

export default QuestHOC(QuestPartners)
